import { Type } from 'class-transformer';
import { EnaioWorkItemParameter } from 'src/modules/enaio/shared/EnaioWorkItemParameter';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Point } from 'src/modules/utils/misc/point.model';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Bounds } from 'src/modules/utils/shared/bounds.model';
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { SmTableCellEditorComponent } from '../components/sm-table-cell-editor/sm-table-cell-editor.component';
import { SmTableComponent } from "../components/sm-table/sm-table.component";
import { FrontendFieldAddon } from '../models/frontend-field-addon.model';
import { FrontendFieldGuiHint } from '../models/frontend-field-gui-hint.model';
import { FrontendFieldType } from './FrontendFieldType';
import { TableColumn } from './TableColumn';
import { FrontendFieldError } from "./FrontendFieldError";
import { FrontendFieldListItem } from './FrontendFieldListItem';
import { FrontendForm } from './FrontendForm';
import { TableCellType } from './TableCellType';
import { Dates } from 'src/modules/utils/shared/dates';
import { ButtonSeverity } from '@openng/optimus-ui/button';
import { DateOnly } from '../models/date-only.model';
import { FrontendFieldValueChangedEvent } from '../models/frontend-field-value-changed-event.model';

export class FrontendField {

    //netgen start
    id = "";
    title = "";
    type: FrontendFieldType = FrontendFieldType.string;
    mandatory = false;
    enabled = true;
    visible = true;
    guiGroup = "";
    guiGroupOwnLabel = true;
    guiWidthInGroup = 100;
    guiGroupInline = false;
    titleAfter = "";
    labelLeft = false;
    hasLabel = true;
    hasControl = true;
    maxLength: number = null;
    upperCase = false;
    dataSourceId: string;
    callValueChangedAfterLoad = false;
    dropdownEditable = false;
    dropdownCanFilter = false;
    dropdownFilterBy: string;
    dropdownAutoComplete = false;
    dropdownButtonVisible = true;
    dropdownOptionLabel = "label";
    dropdownOptionValue = "value";
    fixedHeight: string;
    textAreaAutoHeight = false;
    textAreaMinHeight: string;
    textAreaMaxHeight: string;
    allowedFileExtensions: string[];
    maxFileSizeBytes: number = null;
    titleAsPlaceholder = false;
    @Type(() => FrontendFieldListItem)
    listItems: FrontendFieldListItem[];
    @Type(() => TableColumn)
    tableColumns: TableColumn[];
    isEmailAddress = false;
    datePickerMonth = false;
    datePickerYear = false;
    dateIntervalToField: string;
    dateIntervalFromField: string;
    dateIntervalSameYear = false;
    dateIntervalSameMonth = false;
    numberAllowEmpty = false;
    minValue = 0;
    maxValue: number = null;
    minValueInclusive = true;
    maxValueInclusive = true;
    tableAllowRowDrag = false;
    tableAddRowOnTabInLastCell = false;
    tableCompact: boolean = null;
    value: any;
    @Type(() => FrontendFieldError)
    errorsFromScript: FrontendFieldError[];
    //netgen end

    form: FrontendForm;
    
    dateIntervalEmptyAllowed = true;
   
    placeholder: string = null;
    dropdownAutoCompleteMaxItems: number = null;
    dropdownAutoCompleteMinLength = 0;
    labelRightAligned = false;
    emptyValues: any[];
    datePickerUseDateOnly = false;
    checkBoxTriState = false;
    icon = "";
    severity: ButtonSeverity;
    tabOrder = 0;
    fixedLabelBounds: Bounds = null;
    fixedControlBounds: Bounds = null;
    tooltip: string = null;
    tooltipStyleClasses: string = null;
    tooltipOnControl = false;
    monospaced = false;
    textAreaWordWrap = true;
    listItemValuePath: string;
    spinnerStep = 1;
    allowOnlySpinnerStepValues = false;
    labelStyleClass: string = null;
    controlStyleClass: string = null;
    
    children: FrontendField[] = [];

    tableCanAddRow = true;
    tableCanRemoveRow = true;
    tableFloatingFilter = false;
    tableStopEditingWhenCellsLoseFocus = false;
    tableDisabledCells: { row: number; column: number }[];
    tableInvalidCells: OrdinaryObject<string> = {};
    tableMustRefresh = false;
    fullScreenRowEditorOnMobile = false;
    
    regexMask = "";
    radioButtonGroup = "";
    radioButtonValue: any = "";
    dropdownWordWrap = false;
    listBoxCheckBoxes = false;
    passwordFeedback = false;
    predefinedColors: number[];
    indent = 0;

    noBuiltInValidation = false;

    enaioSectionName = "";
    submitButton = false;

    guiHints: FrontendFieldGuiHint[] = [];
    addOnButtons: FrontendFieldAddon[] = null;
    
    style: OrdinaryObject;

    componentType: any = null; // type = Type<any>
    enaioSource: any = null; //EnaioGuiField = null;
        
    pageSelected = "0";

    requestFocus = false;
    //Keine wirklich schöne Lösung mit dem Request, aber keine bessere Idee zur Zeit
    tableSelectedRow = -1;
    tableSelectedRowRequest = -1;
    
    noMapping = false;

    context: any;
    component: any;
    
    errors: FrontendFieldError[] = null;

    userDefinedData: OrdinaryObject;

    tableCellEditor: SmTableCellEditorComponent;
    guiComponent: any;

    generateEmptyTableItem: () => any = () => ({});

    validator: (field: FrontendField, form: FrontendForm) => FrontendFieldError[];

    onAutoComplete: (query: string) => Promise<FrontendFieldListItem[]>;
    onClick: (event: MouseEvent) => any;
    onKeyPress: (event: KeyboardEvent) => any;
    onValueChanged: (evt: FrontendFieldValueChangedEvent) => void;
    onBeforeAddRow: () => any;
    onAddOnClick: (item: FrontendFieldAddon, field: FrontendField, event: MouseEvent) => any;

    toForm: (value: any, on: any) => any;
    fromForm: (value: any, on: any) => void;

    constructor(id: string, title: string, type: FrontendFieldType, props: Partial<FrontendField> = null) {
        this.id = id;
        this.title = title;
        this.type = type;
        if (props != null) {
            Object.assign(this, props);
            if ("value" in props) {
                this.setValue(props.value);
            }
        }
        Object.defineProperty(this, "guiComponent", {
            value: this.guiComponent,
            enumerable: false,
            writable: true
        });
        Object.defineProperty(this, "tableCellEditor", {
            value: this.tableCellEditor,
            enumerable: false,
            writable: true
        });
        Object.defineProperty(this, "form", {
            value: this.form,
            enumerable: false,
            writable: true
        });
    }

    getDescendents(): FrontendField[] {
        return Arrays.explode(this.children, child => [child, ...child.getDescendents()]);
    }

    getGroupedChildren(): FrontendField[][] {
        let result: FrontendField[][] = [];
        for (let field of this.children) {
            let group = Utils.isNoe(field.guiGroup) ? null : result.find(g => g[0].guiGroup == field.guiGroup);
            if (group == null) {
                result.push([field]);
            }
            else {
                group.push(field);
            }
        }
        return result;
    }

    setComponent(component: any /* type = Type<any>*/): this {
        this.componentType = component;
        return this;
    }

    setCellValid(row: number, column: number, valid: boolean, errorMessage?: string): void {
        let key = row + "," + column;
        if (valid) {
            delete this.tableInvalidCells[key];
        }
        else {
            this.tableInvalidCells[key] = errorMessage;
        }
    }

    isCellValid(row: number, column: number): boolean {
        return !Objects.hasProperty(this.tableInvalidCells, row + "," + column);
    }

    getCellErrorMessage(row: number, column: number): string {
        return Objects.getSafe(this.tableInvalidCells, row + "," + column);
    }

    getCellStyleClass(value: any, row: number, column: number): string {
        let columnId = this.tableColumns?.[column]?.id;
        return this.getErrors(true, true, false).some(error => columnId != null && error.subItem == row + "," + columnId) || !this.isCellValid(row, column)
            ? "xbg-red-100 sm-table-error-cell"
            : !this.isTableCellEnabled(row, column) ? "xbg-gray-100" : null;
    }

    getErrors(withCalculated = true, forToolTip = false, includeWarnings = true): FrontendFieldError[] {
        let result = (withCalculated ? this.errors : null) ?? this.errorsFromScript ?? [];
        if (!forToolTip && Utils.isNoe(result)) {
            let invalidCells = Objects.getPropertyNames(this.tableInvalidCells);
            if (invalidCells.length > 0) {
                let pos = Strings.splitOnce(invalidCells[0], ",");
                result.push(new FrontendFieldError(this.id, (Arrays.getSafe(this.tableColumns, Utils.toNumber(pos[1]))?.title ?? pos[1]) + ", Zeile  " + (Utils.toNumber(pos[0]) + 1) + ": " + this.tableInvalidCells[invalidCells[0]]));
            }
        }
        return includeWarnings ? result : result.filter(error => !error.warning);
    }

    isFixedPosition(): boolean {
        return this.fixedLabelBounds != null || this.fixedControlBounds != null;
    }

    getPlaceholder(): string {
        return this.titleAsPlaceholder ? this.title : this.placeholder;
    }

    getMaxPosition(): Point {
        let result = new Point(0, 0);
        if (this.fixedLabelBounds != null) {
            result = result.max(this.fixedLabelBounds.getRight(), this.fixedLabelBounds.getBottom());
        }
        if (this.fixedControlBounds != null) {
            result = result.max(this.fixedControlBounds.getRight(), this.fixedControlBounds.getBottom());
        }
        return result;
    }

    updateValueFromGui(): void {
        if (this.component != null) {
            this.value = this.component.getValue();
        }
    }

    getUpdatedValue(): any {
        this.updateValueFromGui();
        return this.value;
    }

    isGuiOneLine(): boolean {
        return this.type == FrontendFieldType.bool || this.type == FrontendFieldType.radioButton;
    }

    getFrontendValueForWorkItem(param: EnaioWorkItemParameter): any {
        let value = param.getPlainValue();
        if (this.type == FrontendFieldType.bool) {
            return Utils.toBool(value);
        }
        else {
            return value;
        }
    }

    validate(form: FrontendForm): FrontendFieldError[] {
        if (this.visible) {
            if (!this.noBuiltInValidation) {
                if (this.enabled && this.mandatory && (this.emptyValues != null ? this.emptyValues.includes(this.value) : Utils.isNoe(this.value))) {
                    return [new FrontendFieldError(this.id, this.title + " ist ein Pflichtfeld")];
                }
                if (this.minValue != null && (this.minValueInclusive ? this.value < this.minValue : this.value <= this.minValue)) {
                    return [new FrontendFieldError(this.id, this.title + " muss größer " + (this.minValueInclusive ? "oder gleich " : "") + this.minValue + " sein")];
                }
                if (this.maxValue != null && (this.maxValueInclusive ? this.value > this.maxValue : this.value >= this.maxValue)) {
                    return [new FrontendFieldError(this.id, this.title + " muss kleiner " + (this.maxValueInclusive ? "oder gleich " : "") + this.maxValue + " sein")];
                }
                if (this.isEmailAddress && !Utils.isNoe(this.value) && !Utils.isEmailAddressValid(this.getValueString())) {
                    return [new FrontendFieldError(this.id, this.title + " ist keine gültige E-Mail-Adresse")];
                }
                if (this.validator != null) {
                    let result = this.validator(this, form);
                    if (!Utils.isNoe(result)) {
                        return result;
                    }
                }
                if (!Utils.isNoe(this.dateIntervalToField)) {
                    let toField = form.getField(this.dateIntervalToField);
                    if (toField.visible) {
                        let toValue = form.getValue(this.dateIntervalToField);
                        let thisValue = this.getValue();

                        if (toValue instanceof Date && thisValue instanceof Date) {
                            if (this.dateIntervalEmptyAllowed && thisValue > toValue) {
                                return [new FrontendFieldError(this.id, this.title + " darf nicht nach " + toField.title + " liegen")];
                            }
                            if (!this.dateIntervalEmptyAllowed && thisValue >= toValue) {
                                return [new FrontendFieldError(this.id, this.title + " muss vor " + toField.title + " liegen")];
                            }
                            if (this.dateIntervalSameYear && thisValue.getFullYear() != toValue.getFullYear()) {
                                return [new FrontendFieldError(this.id, this.title + " muss im selben Kalenderjahr wie " + toField.title + " liegen")];
                            }
                            if (this.dateIntervalSameMonth && (thisValue.getFullYear() != toValue.getFullYear() || thisValue.getMonth() != toValue.getMonth())) {
                                return [new FrontendFieldError(this.id, this.title + " muss im selben Kalendermonat wie " + toField.title + " liegen")];
                            }
                        }
                    }
                }
                if (!Utils.isNoe(this.dateIntervalFromField)) {
                    let fromField = form.getField(this.dateIntervalFromField);
                    if (fromField.visible) {
                        let fromValue = form.getValue(this.dateIntervalFromField);
                        let thisValue = this.getValue();

                        if (fromValue instanceof Date && thisValue instanceof Date) {
                            if (this.dateIntervalEmptyAllowed && thisValue < fromValue) {
                                return [new FrontendFieldError(this.id, this.title + " darf nicht vor " + fromField.title + " liegen")];
                            }
                            if (!this.dateIntervalEmptyAllowed && thisValue <= fromValue) {
                                return [new FrontendFieldError(this.id, this.title + " muss nach " + fromField.title + " liegen")];
                            }
                            if (this.dateIntervalSameYear && thisValue.getFullYear() != fromValue.getFullYear()) {
                                return [new FrontendFieldError(this.id, this.title + " muss im selben Kalenderjahr wie " + fromField.title + " liegen")];
                            }
                            if (this.dateIntervalSameMonth && (thisValue.getFullYear() != fromValue.getFullYear() || thisValue.getMonth() != fromValue.getMonth())) {
                                return [new FrontendFieldError(this.id, this.title + " muss im selben Kalendermonat wie " + fromField.title + " liegen")];
                            }
                        }
                    }
                }
            }
            return this.getErrors(false);
        }
        return [];
    }

    setValue(value: any, callValueChangedByUser?: boolean, on?: any): void {
        this.value = this.toForm != null ? this.toForm(value, on) : value;
        if (this.type == FrontendFieldType.date && this.datePickerUseDateOnly && Dates.is(this.value)) {
            this.value = Dates.withoutTime(this.value as Date);
        }
        else if (this.type == FrontendFieldType.date && this.value instanceof DateOnly) {
            this.value = this.value.toDate();
        }
        else if (this.type == FrontendFieldType.dropdownMulti && this.dropdownAutoComplete && this.listItems != null && Arrays.is(this.value)) {
            this.value = (this.value as any[]).map(item => this.listItems.find(li => item == li.value));
        }
        else if (this.type == FrontendFieldType.dropdown && !this.dropdownEditable && !(this.value instanceof FrontendFieldListItem)) {
            this.value = this.listItemValuePath == null || this.value == null ? this.value : Objects.getPropertyNested(this.value, this.listItemValuePath);
        }
        this.valueWasChanged(callValueChangedByUser != null ? new FrontendFieldValueChangedEvent(this.id, this.value, null, false, callValueChangedByUser) : null);        
    }

    fireValueChanged(byUser: boolean): void {
        if (this.onValueChanged != null) {
            this.onValueChanged(FrontendFieldValueChangedEvent.simple(this, byUser));
        }
    }

    valueWasChanged(evt: FrontendFieldValueChangedEvent, validateIfValidateOnChange = true): void {
        if (evt != null && this.onValueChanged != null) {
            this.onValueChanged(evt);
        }
        if (validateIfValidateOnChange && this.form?.validateOnChange) {
            this.form.validate(false);
        }
    }

    getValue(useDropdownInternalObject = true, to: any = null): unknown {
        let v = this.value;
        if (this.fromForm != null) {
            v = this.fromForm(v, to);
        }

        if (this.type == FrontendFieldType.dropdown) {
            if (v instanceof FrontendFieldListItem) {
                return useDropdownInternalObject ? v.internalObject ?? v.value : v.value;
            }
            else {
                return this.listItemValuePath == null ? v : this.listItems.find(item => Objects.getPropertyNested(item.internalObject, this.listItemValuePath) == v)?.internalObject;
            }
        }
        else if (this.type == FrontendFieldType.bool && !this.checkBoxTriState && v == null) {
            return false;
        }
        else if (this.type == FrontendFieldType.date) {
            if (this.datePickerUseDateOnly) {
                return Dates.is(v) ? DateOnly.fromDate(v as Date) : null;
            }
            else {
                return Dates.is(v) ? Dates.toUtc(Dates.withoutTime(v as Date)) : null;
            }
        }
        else {
            return v;
        }
    }

    getValueBool(): boolean {
        return this.getValue() as boolean;
    }

    getValueDate(): Date {
        let result = this.getValue();
        return result == "" ? null : result as Date;
    }

    getValueNumber(): number {
        return this.getValue() as number;
    }

    getValueString(): string {
        return this.getValue() as string;
    }

    isTableCellEnabled(row: number, column: number): boolean {
        return !this.tableDisabledCells?.some(cell => cell.row == row && cell.column == column);
    }

    hasLabelAdvanced(): boolean {
        return this.hasLabel && FrontendFieldType.hasLabel(this.type);
    }

    toTableColumn(): TableColumn {
        let result = new TableColumn(this.id, this.title, TableCellType.string, -1);
        switch (this.type) {
            case FrontendFieldType.int:
                result.cellType = TableCellType.int;
                break;
            case FrontendFieldType.decimal:
                result.cellType = TableCellType.decimal;
                break;
            case FrontendFieldType.bool:
                result.cellType = TableCellType.bool;
                break;
            case FrontendFieldType.date:
                result.cellType = TableCellType.date;
                break;
        }
        return result;
    }

    focus(): void {
        GuiUtils.angularTimer(() => {
            let c = this.guiComponent ?? this.component;
            if (c != null) {
                if (Utils.isFunction(c.focus)) {
                    try {
                        c.focus();
                    }
                    catch {
                        //Cannot read properties of null (reading 'focus') Passiert manchmal - unbekannt warum
                    }
                }
                c.nativeElement?.focus();
                //für Multiselekt;
                c.containerViewChild?.nativeElement?.querySelector('input')?.focus();
            }
        });
    }

    finishEditing(): void {
        if (this.type == FrontendFieldType.table) {
            (this.guiComponent as SmTableComponent)?.stopCellEditing();
        }
    }

}
